import { useState } from 'react';
import banner from '../assets/cozyroom.png';
import {
  challengeLabel,
  hasAccess,
  respondToChallenge,
  signIn,
  type Challenge,
  type Session,
  type SignInResult,
} from '../lib/auth';

export function Login({ onSignedIn }: { onSignedIn: (session: Session) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [answer, setAnswer] = useState('');
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  /** 挑战可能连着来（比如改密码之后紧接着要验证码），所以统一在这里收敛 */
  const advance = (result: SignInResult) => {
    if (result.kind === 'challenge') {
      setChallenge(result.challenge);
      setAnswer('');
      setError('');
      return;
    }
    if (!hasAccess(result.session)) {
      setError('This account is not in the editor or super-admin group, so it cannot upload.');
      return;
    }
    onSignedIn(result.session);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      advance(challenge
        ? await respondToChallenge(challenge, answer)
        : await signIn(email.trim(), password));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const restart = () => {
    setChallenge(null);
    setAnswer('');
    setPassword('');
    setError('');
  };

  return (
    <div className="login-wrap">
      <form className="login-card" onSubmit={submit}>
        <img className="login-banner" src={banner} alt="" />

        <div className="login-body">
          <h1>Cozy Room Music Console</h1>
          <p className="sub">
            {challenge ? challenge.prompt : 'Sign in with your Minine account'}
          </p>

          {!challenge && (
            <>
              <div className="field">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                  autoFocus
                  required
                />
              </div>
              <div className="field">
                <label htmlFor="password">Password</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </div>
            </>
          )}

          {challenge?.inputKind === 'select' && (
            <div className="field">
              <label>Verification method</label>
              {(challenge.options ?? []).map((option) => (
                <button
                  key={option}
                  type="button"
                  className={answer === option ? 'primary' : ''}
                  style={{ width: '100%', marginBottom: 6 }}
                  onClick={() => setAnswer(option)}
                >
                  {challengeLabel(option)}
                </button>
              ))}
              {!challenge.options?.length && (
                <p className="hint">Cognito offered no methods, so sign-in cannot continue.</p>
              )}
            </div>
          )}

          {challenge?.inputKind === 'code' && (
            <div className="field">
              <label htmlFor="answer">Code</label>
              <input
                id="answer"
                value={answer}
                onChange={(e) => setAnswer(e.target.value.trim())}
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                required
              />
              <p className="hint" style={{ marginTop: 6 }}>
                Challenge: {challengeLabel(challenge.name)}
              </p>
            </div>
          )}

          {challenge?.inputKind === 'password' && (
            <div className="field">
              <label htmlFor="answer">New password</label>
              <input
                id="answer"
                type="password"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                autoComplete="new-password"
                autoFocus
                required
              />
            </div>
          )}

          <button
            className="primary"
            type="submit"
            disabled={busy || (challenge?.inputKind === 'select' && !answer)}
            style={{ width: '100%' }}
          >
            {busy ? 'Working…' : challenge ? 'Continue' : 'Sign in'}
          </button>

          {challenge && (
            <button
              type="button"
              className="ghost"
              onClick={restart}
              style={{ width: '100%', marginTop: 8 }}
            >
              Start over
            </button>
          )}

          {error && <p className="error-text">{error}</p>}
        </div>
      </form>
    </div>
  );
}
